import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import client from "../api/client";

interface EffectsSettings {
  binaryRain: boolean;
  surveillance: boolean;
  videoUrl: string;
}

interface SettingsContextType {
  effects: EffectsSettings;
  loading: boolean;
  toggleEffect: (key: "binaryRain" | "surveillance") => void;
  setEffect: (key: "binaryRain" | "surveillance", value: boolean) => void;
  setVideoUrl: (url: string) => void;
}

const SettingsContext = createContext<SettingsContextType | null>(null);

const DEFAULT_EFFECTS: EffectsSettings = {
  binaryRain: false,
  surveillance: false,
  videoUrl: "",
};

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [effects, setEffects] = useState<EffectsSettings>(DEFAULT_EFFECTS);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await client.get("/settings/public");
      const data = res.data?.data ?? {};
      setEffects({
        binaryRain: data.effects_binary_rain === "true",
        surveillance: data.effects_surveillance === "true",
        videoUrl: data.effects_video_url || "",
      });
    } catch {
      // keep defaults on failure
    }
  }, []);

  useEffect(() => {
    fetchSettings().finally(() => setLoading(false));
  }, [fetchSettings]);

  const toggleEffect = useCallback((key: "binaryRain" | "surveillance") => {
    setEffects((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setEffect = useCallback((key: "binaryRain" | "surveillance", value: boolean) => {
    setEffects((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setVideoUrl = useCallback((url: string) => {
    setEffects((prev) => ({ ...prev, videoUrl: url }));
  }, []);

  return (
    <SettingsContext.Provider value={{ effects, loading, toggleEffect, setEffect, setVideoUrl }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useEffects() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useEffects must be used within SettingsProvider");
  return ctx;
}
